import React, { useState, useEffect, useCallback } from 'react';
import BiologicalLayout from '@/components/layout/BiologicalLayout';
import { useAuth, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Brain, ChevronRight, ChevronLeft, Check, Play, Clock, ArrowRight,
  AlertCircle, Loader2, Activity, Shield, Zap, Heart, MessageSquare,
  TrendingUp, BarChart3, BookOpen
} from 'lucide-react';
import { UnderstandThis } from '@/components/UnderstandThis';
import { TwinFloSubscriberCard } from '@/components/TwinFloCards';

// ═══════════════════════════════════════════════════════════════
// CHECK-IN FLOW (5 steps + output)
// ═══════════════════════════════════════════════════════════════

function CheckInFlow({ options, onComplete, onCancel }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ current_phase: '', strongest_feeling: '', trigger_type: '', need_type: '', note_text: '' });
  const [submitting, setSubmitting] = useState(false);

  const steps = [
    { key: 'current_phase', title: 'What phase are you in?', subtitle: 'Your current medication/body timing', items: options.phases },
    { key: 'strongest_feeling', title: 'What feels strongest right now?', subtitle: 'The dominant sensation or state', items: options.feelings },
    { key: 'trigger_type', title: 'Is anything triggering this?', subtitle: 'What may be causing or amplifying it', items: options.triggers },
    { key: 'need_type', title: 'What do you need right now?', subtitle: 'What would help most', items: options.needs },
  ];

  const select = (key, value) => {
    setAnswers(p => ({ ...p, [key]: value }));
    if (step < 3) setTimeout(() => setStep(s => s + 1), 200);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/mindflo/check-in`, answers);
      onComplete(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Check-in failed');
    } finally { setSubmitting(false); }
  };

  if (step < 4) {
    const s = steps[step];
    return (
      <div className="max-w-lg mx-auto px-4 py-8" data-testid="checkin-flow">
        <div className="flex items-center gap-2 mb-8">
          {[0,1,2,3].map(i => (
            <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i <= step ? 'bg-[#8C6B55]' : 'bg-[#EBE8E3]'}`} />
          ))}
        </div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8681] font-['Work_Sans'] mb-2">Step {step + 1} of 5</p>
        <h2 className="text-xl font-['Outfit'] font-light text-[#2C2A29] mb-1" data-testid="checkin-step-title">{s.title}</h2>
        <p className="text-xs text-[#8A8681] font-['Work_Sans'] mb-6">{s.subtitle}</p>
        <div className="grid grid-cols-1 gap-2">
          {s.items.map(item => (
            <button key={item.key} onClick={() => select(s.key, item.key)}
              className={`w-full text-left px-4 py-3.5 rounded-xl border transition-all text-sm font-['Work_Sans'] ${
                answers[s.key] === item.key
                  ? 'border-[#8C6B55] bg-[#8C6B55]/5 text-[#2C2A29]'
                  : 'border-[#EBE8E3] text-[#5C5955] hover:border-[#C89B7B]/30 hover:bg-[#F8F6F3]'
              }`}
              data-testid={`checkin-option-${item.key}`}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className="flex items-center justify-between mt-8">
          <button onClick={step > 0 ? () => setStep(s => s - 1) : onCancel} className="text-xs text-[#8A8681] hover:text-[#5C5955] font-['Work_Sans']">
            {step > 0 ? <><ChevronLeft className="w-3 h-3 inline mr-1" />Back</> : 'Cancel'}
          </button>
          {answers[s.key] && (
            <button onClick={() => setStep(s => s + 1)} className="text-xs text-[#8C6B55] font-['Work_Sans'] font-medium flex items-center gap-1">
              Next <ChevronRight className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // Step 5: Note
  return (
    <div className="max-w-lg mx-auto px-4 py-8" data-testid="checkin-note-step">
      <div className="flex items-center gap-2 mb-8">
        {[0,1,2,3].map(i => <div key={i} className="h-1 flex-1 rounded-full bg-[#8C6B55]" />)}
      </div>
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8681] font-['Work_Sans'] mb-2">Step 5 of 5</p>
      <h2 className="text-xl font-['Outfit'] font-light text-[#2C2A29] mb-1">Anything else to note?</h2>
      <p className="text-xs text-[#8A8681] font-['Work_Sans'] mb-6">Optional — add context if it helps</p>
      <Textarea value={answers.note_text} onChange={e => setAnswers(p => ({ ...p, note_text: e.target.value }))}
        placeholder="Add a note..." className="bg-white border-[#EBE8E3] text-[#2C2A29] text-sm min-h-[80px] mb-6" data-testid="checkin-note" />
      <div className="flex items-center justify-between">
        <button onClick={() => setStep(3)} className="text-xs text-[#8A8681] hover:text-[#5C5955] font-['Work_Sans']">
          <ChevronLeft className="w-3 h-3 inline mr-1" />Back
        </button>
        <Button onClick={handleSubmit} disabled={submitting}
          className="bg-[#2C2A29] text-white hover:bg-[#3C3A39] rounded-full px-6" data-testid="checkin-submit">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Complete Check-in'}
        </Button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CHECK-IN OUTPUT (shown after submit)
// ═══════════════════════════════════════════════════════════════

function CheckInOutput({ result, onViewSession, onDismiss }) {
  const { state_info, pattern_info, recommended_session, urgency, counselor_review_needed } = result;
  return (
    <div className="max-w-lg mx-auto px-4 py-8 space-y-5" data-testid="checkin-output">
      <div className="text-center mb-6">
        <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: (state_info?.color || '#8A8681') + '15' }}>
          <Check className="w-5 h-5" style={{ color: state_info?.color }} />
        </div>
        <h2 className="text-lg font-['Outfit'] font-light text-[#2C2A29]">Check-in recorded</h2>
      </div>
      {/* State card */}
      <div className="rounded-2xl border border-[#EBE8E3] p-5" data-testid="output-state">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8681] font-['Work_Sans'] mb-2">Current State</p>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: state_info?.color }} />
          <p className="text-base font-['Outfit'] text-[#2C2A29]">{state_info?.label}</p>
        </div>
        <p className="text-xs text-[#5C5955] font-['Work_Sans'] leading-relaxed">{state_info?.description}</p>
        <div className="mt-2"><UnderstandThis state={result.state} inline /></div>
      </div>
      {/* Pattern card */}
      {pattern_info && (
        <div className="rounded-2xl border border-[#EBE8E3] p-5" data-testid="output-pattern">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8681] font-['Work_Sans'] mb-2">What May Be Happening</p>
          <p className="text-sm font-['Work_Sans'] font-medium text-[#2C2A29] mb-1.5">{pattern_info.label}</p>
          <p className="text-xs text-[#5C5955] font-['Work_Sans'] leading-relaxed">{pattern_info.explanation}</p>
          <div className="mt-2"><UnderstandThis pattern={result.pattern} inline /></div>
        </div>
      )}
      {/* Recommended session */}
      {recommended_session && (
        <div>
          <button onClick={() => onViewSession(recommended_session)} className="w-full text-left rounded-2xl border border-[#8C6B55]/20 p-5 hover:bg-[#8C6B55]/[0.03] transition-colors" data-testid="output-session">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#8C6B55]/60 font-['Work_Sans'] mb-2">Recommended Session</p>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-['Work_Sans'] font-medium text-[#2C2A29]">{recommended_session.title}</p>
                <p className="text-xs text-[#8A8681] font-['Work_Sans'] mt-0.5">{recommended_session.estimated_duration_minutes} min &middot; {recommended_session.mode}</p>
              </div>
              <div className="w-9 h-9 rounded-full bg-[#8C6B55] flex items-center justify-center">
                <Play className="w-4 h-4 text-white ml-0.5" fill="white" />
              </div>
            </div>
          </button>
          {/* Why this session explanation */}
          {result.why_this_session && (
            <div className="mt-2 px-4 py-3 rounded-xl bg-[#5C6B8A]/[0.04] border-l-2 border-[#5C6B8A]/20" data-testid="why-this-session">
              <p className="text-[10px] uppercase tracking-[0.15em] text-[#5C6B8A]/50 font-['Work_Sans'] mb-1">Why this session</p>
              <p className="text-xs text-[#5C6B8A] font-['Work_Sans'] leading-relaxed">{result.why_this_session}</p>
            </div>
          )}
        </div>
      )}
      {result.escalation_flag && (
        <div className="rounded-xl bg-[#C85C5C]/5 border border-[#C85C5C]/15 p-4 text-center">
          <p className="text-xs text-[#C85C5C] font-['Work_Sans']">This may need guided support — your care team has been notified</p>
        </div>
      )}
      {counselor_review_needed && !result.escalation_flag && (
        <div className="rounded-xl bg-[#C85C5C]/5 border border-[#C85C5C]/15 p-4 text-center">
          <p className="text-xs text-[#C85C5C] font-['Work_Sans']">Your care team has been flagged for review</p>
        </div>
      )}
      <button onClick={onDismiss} className="w-full text-center text-xs text-[#8A8681] hover:text-[#5C5955] font-['Work_Sans'] py-2" data-testid="output-done">
        Done
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SESSION PLAYER (step-by-step)
// ═══════════════════════════════════════════════════════════════

function SessionPlayer({ session, onComplete, onBack }) {
  const [currentStep, setCurrentStep] = useState(-1); // -1 = intro
  const [afterNote, setAfterNote] = useState('');
  const [completing, setCompleting] = useState(false);
  const [started, setStarted] = useState(false);
  const steps = session.session_steps || [];

  const handleStart = async () => {
    setStarted(true);
    try { await axios.post(`${API}/mindflo/sessions/${session.id}/start`); } catch {}
    setCurrentStep(0);
  };

  const handleComplete = async () => {
    setCompleting(true);
    try {
      await axios.post(`${API}/mindflo/sessions/${session.id}/complete`, { after_note: afterNote });
      toast.success('Session completed');
      onComplete();
    } catch { toast.error('Failed to record completion'); }
    finally { setCompleting(false); }
  };

  // Intro
  if (currentStep === -1) {
    return (
      <div className="max-w-lg mx-auto px-4 py-8" data-testid="session-intro">
        <button onClick={onBack} className="text-[10px] uppercase tracking-[0.2em] text-[#8A8681] hover:text-[#5C5955] font-['Work_Sans'] mb-6 flex items-center gap-1">
          <ChevronLeft className="w-3 h-3" /> Back
        </button>
        <Badge variant="outline" className="border-[#EBE8E3] text-[#8A8681] text-[9px] mb-3 capitalize">{session.type?.replace('_', ' ')}</Badge>
        <h1 className="text-2xl font-['Outfit'] font-light text-[#2C2A29] mb-2" data-testid="session-title">{session.title}</h1>
        <p className="text-sm text-[#5C5955] font-['Work_Sans'] leading-relaxed mb-2">{session.description}</p>
        <p className="text-xs text-[#8A8681] font-['Work_Sans'] mb-8">{session.estimated_duration_minutes} minutes &middot; {steps.length} steps</p>
        <div className="rounded-xl bg-[#F8F6F3] border border-[#EBE8E3] p-4 mb-8">
          <p className="text-[10px] uppercase tracking-[0.15em] text-[#8A8681] font-['Work_Sans'] mb-1.5">Objective</p>
          <p className="text-sm text-[#2C2A29] font-['Work_Sans'] leading-relaxed">{session.objective}</p>
        </div>
        <Button onClick={handleStart} className="w-full bg-[#2C2A29] text-white hover:bg-[#3C3A39] rounded-full py-6" data-testid="session-start-btn">
          <Play className="w-4 h-4 mr-2" fill="white" /> Begin Session
        </Button>
      </div>
    );
  }

  // Completion/reflection
  if (currentStep >= steps.length) {
    return (
      <div className="max-w-lg mx-auto px-4 py-8" data-testid="session-reflection">
        <div className="text-center mb-6">
          <div className="w-12 h-12 rounded-full bg-[#4E7A6B]/10 mx-auto mb-3 flex items-center justify-center">
            <Check className="w-5 h-5 text-[#4E7A6B]" />
          </div>
          <h2 className="text-lg font-['Outfit'] font-light text-[#2C2A29]">Session Complete</h2>
        </div>
        {session.completion_reflection_prompt && (
          <div className="rounded-xl bg-[#F8F6F3] border border-[#EBE8E3] p-4 mb-4">
            <p className="text-sm text-[#2C2A29] font-['Work_Sans'] leading-relaxed">{session.completion_reflection_prompt}</p>
          </div>
        )}
        <Textarea value={afterNote} onChange={e => setAfterNote(e.target.value)}
          placeholder="How do you feel now? (optional)" className="bg-white border-[#EBE8E3] text-[#2C2A29] text-sm min-h-[60px] mb-4" data-testid="session-after-note" />
        {session.after_effect_prompt && (
          <p className="text-[11px] text-[#8C6B55] font-['Work_Sans'] italic mb-6">{session.after_effect_prompt}</p>
        )}
        <Button onClick={handleComplete} disabled={completing} className="w-full bg-[#4E7A6B] text-white hover:bg-[#3E6A5B] rounded-full" data-testid="session-finish-btn">
          {completing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Finish'}
        </Button>
      </div>
    );
  }

  // Active step
  const s = steps[currentStep];
  return (
    <div className="max-w-lg mx-auto px-4 py-8" data-testid="session-step">
      <div className="flex items-center gap-2 mb-6">
        {steps.map((_, i) => <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i <= currentStep ? 'bg-[#8C6B55]' : 'bg-[#EBE8E3]'}`} />)}
      </div>
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8681] font-['Work_Sans'] mb-2">{s.title}</p>
      <p className="text-[17px] text-[#2C2A29] font-['Work_Sans'] font-light leading-[1.8] mb-6" data-testid="step-instruction">
        {s.instruction_text}
      </p>
      {s.breathing_count && (
        <div className="rounded-xl bg-[#5C6B8A]/5 border border-[#5C6B8A]/10 p-3 mb-4 text-center">
          <p className="text-sm text-[#5C6B8A] font-mono">{s.breathing_count}</p>
        </div>
      )}
      {s.body_cue && (
        <p className="text-xs text-[#8C6B55] font-['Work_Sans'] italic mb-4">{s.body_cue}</p>
      )}
      <div className="flex items-center justify-between mt-8">
        <button onClick={() => setCurrentStep(c => c - 1)} className="text-xs text-[#8A8681] hover:text-[#5C5955] font-['Work_Sans']">
          <ChevronLeft className="w-3 h-3 inline mr-1" />Previous
        </button>
        <Button onClick={() => setCurrentStep(c => c + 1)} className="bg-[#2C2A29] text-white hover:bg-[#3C3A39] rounded-full px-6" data-testid="step-next-btn">
          {currentStep === steps.length - 1 ? 'Complete' : 'Next'} <ChevronRight className="w-3 h-3 ml-1" />
        </Button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SUB-TABS
// ═══════════════════════════════════════════════════════════════

const TABS = [
  { key: 'today', label: 'Today', icon: Brain },
  { key: 'sessions', label: 'My Sessions', icon: Play },
  { key: 'triggers', label: 'My Triggers', icon: AlertCircle },
  { key: 'progress', label: 'My Progress', icon: TrendingUp },
  { key: 'digest', label: 'Weekly', icon: BarChart3 },
];

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

export default function MindFlo() {
  const { user } = useAuth();
  const [tab, setTab] = useState('today');
  const [todayData, setTodayData] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [progress, setProgress] = useState(null);
  const [triggers, setTriggers] = useState([]);
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCheckin, setShowCheckin] = useState(false);
  const [checkinResult, setCheckinResult] = useState(null);
  const [activeSession, setActiveSession] = useState(null);

  const fetchToday = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/mindflo/today`);
      setTodayData(res.data);
    } catch {}
    finally { setLoading(false); }
  }, []);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/mindflo/sessions`);
      setSessions(res.data?.sessions || []);
    } catch {}
  }, []);

  const fetchProgress = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/mindflo/progress`);
      setProgress(res.data);
    } catch {}
  }, []);

  const fetchTriggers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/mindflo/triggers`);
      setTriggers(res.data?.triggers || []);
    } catch {}
  }, []);

  const fetchDigest = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/mindflo/weekly-digest`);
      setDigest(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchToday(); }, [fetchToday]);
  useEffect(() => { if (tab === 'sessions') fetchSessions(); }, [tab, fetchSessions]);
  useEffect(() => { if (tab === 'progress') fetchProgress(); }, [tab, fetchProgress]);
  useEffect(() => { if (tab === 'triggers') fetchTriggers(); }, [tab, fetchTriggers]);
  useEffect(() => { if (tab === 'digest') fetchDigest(); }, [tab, fetchDigest]);

  const handleCheckinComplete = (result) => {
    setCheckinResult(result);
    setShowCheckin(false);
    fetchToday();
  };

  const openSession = (sess) => { setActiveSession(sess); setCheckinResult(null); };

  // Active session player
  if (activeSession) {
    return (
      <BiologicalLayout>
        <SessionPlayer session={activeSession} onComplete={() => { setActiveSession(null); fetchToday(); fetchSessions(); }} onBack={() => setActiveSession(null)} />
      </BiologicalLayout>
    );
  }

  // Check-in flow
  if (showCheckin && todayData?.checkin_options) {
    return (
      <BiologicalLayout>
        <CheckInFlow options={todayData.checkin_options} onComplete={handleCheckinComplete} onCancel={() => setShowCheckin(false)} />
      </BiologicalLayout>
    );
  }

  // Check-in output
  if (checkinResult) {
    return (
      <BiologicalLayout>
        <CheckInOutput result={checkinResult} onViewSession={(s) => openSession(s)} onDismiss={() => setCheckinResult(null)} />
      </BiologicalLayout>
    );
  }

  return (
    <BiologicalLayout>
      <div className="max-w-3xl mx-auto px-4 py-6" data-testid="mindflo-page">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-xl sm:text-2xl font-['Outfit'] font-light tracking-tight text-[#2C2A29]">MindFl&#x14D;</h1>
          <p className="text-[#7C7A77] text-xs mt-1 font-['Work_Sans']">Neuro-behavioral state intelligence</p>
        </div>

        {/* Sub-tabs */}
        <div className="flex gap-1 mb-6 border-b border-[#EBE8E3] pb-0">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-4 py-2.5 text-xs font-['Work_Sans'] border-b-2 transition-colors flex items-center gap-1.5 -mb-[1px] ${
                tab === t.key ? 'border-[#8C6B55] text-[#2C2A29]' : 'border-transparent text-[#8A8681] hover:text-[#5C5955]'
              }`} data-testid={`tab-${t.key}`}>
              <t.icon className="w-3.5 h-3.5" strokeWidth={1.25} /> {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-[#C89B7B] animate-spin" /></div>
        ) : (
          <>
            {/* ════ TODAY TAB ════ */}
            {tab === 'today' && (
              <div className="space-y-5" data-testid="today-tab">
                {/* TwinFlo State Intelligence */}
                <TwinFloSubscriberCard />

                {/* State card (if checked in) */}
                {todayData?.state_info ? (
                  <div>
                    <div className="rounded-2xl border border-[#EBE8E3] p-5" data-testid="state-card">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8681] font-['Work_Sans']">Current State</p>
                        <Badge variant="outline" className="text-[9px]" style={{ borderColor: todayData.state_info.color + '40', color: todayData.state_info.color }}>
                          {todayData.state_info.label}
                        </Badge>
                      </div>
                      <p className="text-sm text-[#5C5955] font-['Work_Sans'] leading-relaxed">{todayData.state_info.description}</p>
                    </div>
                    <div className="mt-1 pl-1">
                      <UnderstandThis state={todayData.state} inline />
                    </div>
                  </div>
                ) : null}

                {/* Check-in CTA */}
                <button onClick={() => setShowCheckin(true)}
                  className="w-full rounded-2xl border-2 border-dashed border-[#8C6B55]/20 p-6 text-center hover:border-[#8C6B55]/40 hover:bg-[#8C6B55]/[0.02] transition-all"
                  data-testid="checkin-cta">
                  <Brain className="w-8 h-8 text-[#8C6B55] mx-auto mb-2" strokeWidth={1} />
                  <p className="text-sm font-['Work_Sans'] text-[#2C2A29] font-medium">
                    {todayData?.has_checkin_today ? 'Update your state' : 'Start your daily check-in'}
                  </p>
                  <p className="text-[11px] text-[#8A8681] font-['Work_Sans'] mt-1">2 minutes &middot; 5 quick questions</p>
                </button>

                {/* Pattern explanation */}
                {todayData?.pattern_info && (
                  <div>
                    <div className="rounded-2xl bg-[#F8F6F3] border border-[#EBE8E3] p-5" data-testid="pattern-card">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8681] font-['Work_Sans'] mb-2">What May Be Happening</p>
                      <p className="text-sm font-['Work_Sans'] font-medium text-[#2C2A29] mb-1.5">{todayData.pattern_info.label}</p>
                      <p className="text-xs text-[#5C5955] font-['Work_Sans'] leading-relaxed">{todayData.pattern_info.explanation}</p>
                    </div>
                    <div className="mt-1 pl-1">
                      <UnderstandThis pattern={todayData.pattern} inline />
                    </div>
                  </div>
                )}

                {/* Recommended session */}
                {todayData?.recommended_session && (
                  <button onClick={() => openSession(todayData.recommended_session)}
                    className="w-full text-left rounded-2xl border border-[#8C6B55]/15 p-5 hover:bg-[#8C6B55]/[0.02] transition-colors"
                    data-testid="recommended-session-card">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#8C6B55]/60 font-['Work_Sans'] mb-2">Recommended Session</p>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-['Work_Sans'] font-medium text-[#2C2A29]">{todayData.recommended_session.title}</p>
                        <p className="text-xs text-[#8A8681] font-['Work_Sans'] mt-0.5">
                          {todayData.recommended_session.estimated_duration_minutes} min &middot; {todayData.recommended_session.mode}
                        </p>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-[#8C6B55] flex items-center justify-center">
                        <Play className="w-4 h-4 text-white ml-0.5" fill="white" />
                      </div>
                    </div>
                  </button>
                )}


                {/* Predictive Stability Block */}
                {todayData?.prediction && todayData.prediction.predictive_risk_level !== 'low' && (
                  <div className="rounded-2xl border border-[#EBE8E3] overflow-hidden" data-testid="predictive-block">
                    <div className="p-5">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8681] font-['Work_Sans']">Upcoming Pattern Risk</p>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[9px]" style={{
                            borderColor: todayData.prediction.predictive_risk_level === 'watch' ? '#D4A84340' : todayData.prediction.predictive_risk_level === 'rising' ? '#C87B7B40' : '#C85C5C40',
                            color: todayData.prediction.predictive_risk_level === 'watch' ? '#D4A843' : todayData.prediction.predictive_risk_level === 'rising' ? '#C87B7B' : '#C85C5C',
                          }}>
                            {todayData.prediction.predictive_risk_level}
                          </Badge>
                          <Badge variant="outline" className="text-[9px] border-[#8A8681]/20 text-[#8A8681]">
                            {todayData.prediction.prediction_confidence} confidence
                          </Badge>
                        </div>
                      </div>

                      {todayData.prediction.predicted_window && (
                        <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-[#D4A843]/[0.06]">
                          <Clock className="w-3.5 h-3.5 text-[#D4A843]" strokeWidth={1.5} />
                          <span className="text-xs text-[#8C6B55] font-['Work_Sans']">Expected window: <strong>{todayData.prediction.predicted_window}</strong></span>
                        </div>
                      )}

                      <p className="text-xs text-[#5C5955] font-['Work_Sans'] leading-relaxed mb-3">{todayData.prediction.why_this_prediction}</p>

                      {todayData.prediction.risk_factors?.length > 0 && (
                        <div className="space-y-1.5 mb-3">
                          {todayData.prediction.risk_factors.map((f, i) => (
                            <div key={i} className="flex items-start gap-2">
                              <AlertCircle className="w-3 h-3 text-[#D4A843] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                              <p className="text-[11px] text-[#5C5955] font-['Work_Sans']">{f}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {todayData.prediction.protective_factors?.length > 0 && (
                        <div className="space-y-1 mb-3">
                          {todayData.prediction.protective_factors.map((p, i) => (
                            <div key={i} className="flex items-start gap-2">
                              <Shield className="w-3 h-3 text-[#4E7A6B] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                              <p className="text-[11px] text-[#4E7A6B] font-['Work_Sans']">{p}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {todayData.prediction.recommended_preventive_action && (
                        <div className="p-3 rounded-xl bg-[#5C6B8A]/[0.04] border-l-2 border-[#5C6B8A]/20">
                          <p className="text-[10px] uppercase tracking-[0.15em] text-[#5C6B8A]/50 font-['Work_Sans'] mb-1">Preventive Recommendation</p>
                          <p className="text-xs text-[#5C6B8A] font-['Work_Sans']">{todayData.prediction.recommended_preventive_action}</p>
                          {todayData.prediction.preventive_timing && (
                            <p className="text-[10px] text-[#5C6B8A]/60 font-['Work_Sans'] mt-1">Timing: {todayData.prediction.preventive_timing}</p>
                          )}
                        </div>
                      )}

                      {todayData.prediction.needs_guided_prevention && (
                        <p className="text-[10px] text-[#8C6B55] font-['Work_Sans'] mt-3 italic">Guided support may be more effective for this risk pattern</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Baseline insights */}
                {todayData?.baseline?.insights?.length > 0 && (
                  <div className="rounded-2xl bg-[#F8F6F3] border border-[#EBE8E3] p-5" data-testid="baseline-insights">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8681] font-['Work_Sans']">Relative to Your Baseline</p>
                      {todayData.baseline.confidence_level && (
                        <Badge variant="outline" className={`text-[9px] ${
                          todayData.baseline.confidence_level === 'high' ? 'border-[#4E7A6B]/30 text-[#4E7A6B]' :
                          todayData.baseline.confidence_level === 'medium' ? 'border-[#D4A843]/30 text-[#D4A843]' :
                          'border-[#8A8681]/30 text-[#8A8681]'
                        }`}>
                          {todayData.baseline.confidence_level} confidence
                        </Badge>
                      )}
                    </div>
                    <div className="space-y-2">
                      {todayData.baseline.insights.map((insight, i) => (
                        <div key={i} className="flex items-start gap-2.5">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#C89B7B] mt-[6px] flex-shrink-0" />
                          <p className="text-xs text-[#5C5955] font-['Work_Sans'] leading-relaxed">{insight}</p>
                        </div>
                      ))}
                    </div>
                    {todayData.baseline.deviation?.outside_baseline && (
                      <div className="mt-3 p-3 rounded-xl bg-[#D4A843]/5 border border-[#D4A843]/10">
                        <p className="text-xs text-[#D4A843] font-['Work_Sans']">{todayData.baseline.deviation.message}</p>
                      </div>
                    )}
                    {/* Mini metrics */}
                    <div className="flex items-center gap-4 mt-3 pt-3 border-t border-[#EBE8E3]">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] text-[#8A8681] font-['Work_Sans']">Stability</span>
                        <span className={`text-[10px] font-mono ${todayData.baseline.stability_direction === 'improving' ? 'text-[#4E7A6B]' : todayData.baseline.stability_direction === 'declining' ? 'text-[#C85C5C]' : 'text-[#8A8681]'}`}>
                          {todayData.baseline.stability_direction === 'improving' ? '↑' : todayData.baseline.stability_direction === 'declining' ? '↓' : '→'} {todayData.baseline.stability_pct}%
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] text-[#8A8681] font-['Work_Sans']">Confidence</span>
                        <span className={`text-[10px] font-mono ${todayData.baseline.confidence_direction === 'improving' ? 'text-[#4E7A6B]' : todayData.baseline.confidence_direction === 'declining' ? 'text-[#C85C5C]' : 'text-[#8A8681]'}`}>
                          {todayData.baseline.confidence_direction === 'improving' ? '↑' : todayData.baseline.confidence_direction === 'declining' ? '↓' : '→'} {todayData.baseline.confidence_pct}%
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] text-[#8A8681] font-['Work_Sans']">Sessions</span>
                        <span className="text-[10px] font-mono text-[#8C6B55]">{todayData.baseline.session_count}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* No checkin yet — empty state */}
                {!todayData?.has_checkin_today && !todayData?.state_info && (
                  <div className="rounded-2xl bg-[#F8F6F3] border border-[#EBE8E3] p-6 text-center">
                    <p className="text-sm text-[#5C5955] font-['Work_Sans']">Complete your check-in to see your state, patterns, and recommended session</p>
                  </div>
                )}
              </div>
            )}

            {/* ════ SESSIONS TAB ════ */}
            {tab === 'sessions' && (
              <div className="space-y-3" data-testid="sessions-tab">
                {sessions.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-sm text-[#8A8681] font-['Work_Sans']">Sessions will appear after your first check-in</p>
                  </div>
                ) : sessions.map(s => (
                  <button key={s.id} onClick={() => openSession(s)}
                    className="w-full text-left rounded-xl border border-[#EBE8E3] p-4 hover:bg-[#F8F6F3] transition-colors"
                    data-testid={`session-card-${s.slug}`}>
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-xl bg-[#8C6B55]/8 flex items-center justify-center flex-shrink-0">
                        <Play className="w-4 h-4 text-[#8C6B55]" strokeWidth={1.5} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-['Work_Sans'] font-medium text-[#2C2A29]">{s.title}</p>
                        <p className="text-[11px] text-[#8A8681] font-['Work_Sans'] truncate">{s.objective}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-[10px] text-[#8A8681] font-['Work_Sans']">{s.estimated_duration_minutes} min</p>
                        {s.completion_count > 0 && <p className="text-[9px] text-[#4E7A6B] font-['Work_Sans']">{s.completion_count}x done</p>}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* ════ PROGRESS TAB ════ */}
            {tab === 'progress' && progress && (
              <div className="space-y-5" data-testid="progress-tab">
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Stability (7d)', value: `${progress.stability_7d}%`, color: '#4E7A6B' },
                    { label: 'Confidence (7d)', value: `${progress.confidence_7d}%`, color: '#5C6B8A' },
                    { label: 'Sessions (7d)', value: progress.session_completions_7d, color: '#8C6B55' },
                    { label: 'Entries (7d)', value: progress.total_entries_7d, color: '#C89B7B' },
                  ].map(m => (
                    <div key={m.label} className="rounded-xl border border-[#EBE8E3] p-4">
                      <p className="text-[10px] uppercase tracking-[0.15em] text-[#8A8681] font-['Work_Sans'] mb-1">{m.label}</p>
                      <p className="text-2xl font-['Outfit'] font-light" style={{ color: m.color }}>{m.value}</p>
                    </div>
                  ))}
                </div>

                {Object.keys(progress.state_distribution_7d || {}).length > 0 && (
                  <div className="rounded-xl border border-[#EBE8E3] p-4">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-[#8A8681] font-['Work_Sans'] mb-3">State Distribution (7d)</p>
                    <div className="space-y-2">
                      {Object.entries(progress.state_distribution_7d).map(([state, count]) => {
                        const total = progress.total_entries_7d || 1;
                        const pct = Math.round(count / total * 100);
                        const info = { stable: '#4E7A6B', unstable: '#D4A843', underdrive: '#5C6B8A', overdrive: '#C87B7B', triggered: '#8C6B55', stressed: '#9B7BC8', recovery_needed: '#C85C5C' };
                        return (
                          <div key={state} className="flex items-center gap-3">
                            <span className="text-xs text-[#5C5955] font-['Work_Sans'] w-24 capitalize">{state.replace('_', ' ')}</span>
                            <div className="flex-1 h-2 bg-[#EBE8E3] rounded-full overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: info[state] || '#8A8681' }} />
                            </div>
                            <span className="text-[10px] text-[#8A8681] font-mono w-8 text-right">{pct}%</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {progress.total_entries_7d === 0 && (
                  <div className="text-center py-12 rounded-xl bg-[#F8F6F3] border border-[#EBE8E3]">
                    <p className="text-sm text-[#8A8681] font-['Work_Sans']">Complete daily check-ins to build your progress data</p>
                  </div>
                )}
              </div>
            )}

            {/* ════ TRIGGERS TAB ════ */}
            {tab === 'triggers' && (
              <div className="space-y-4" data-testid="triggers-tab">
                {triggers.length === 0 ? (
                  <div className="text-center py-12 rounded-xl bg-[#F8F6F3] border border-[#EBE8E3]">
                    <AlertCircle className="w-8 h-8 text-[#C89B7B] mx-auto mb-3" strokeWidth={1} />
                    <p className="text-sm text-[#5C5955] font-['Work_Sans'] mb-1">No triggers mapped yet</p>
                    <p className="text-xs text-[#8A8681] font-['Work_Sans']">Your care team will identify and add your personal triggers as patterns emerge</p>
                  </div>
                ) : triggers.map(t => (
                  <div key={t.id} className="rounded-xl border border-[#EBE8E3] p-4" data-testid={`trigger-${t.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-['Work_Sans'] font-medium text-[#2C2A29]">{t.title}</p>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="border-[#EBE8E3] text-[#8A8681] text-[9px] capitalize">{t.category}</Badge>
                        {t.frequency_count > 0 && <span className="text-[10px] text-[#8C6B55] font-mono">{t.frequency_count}x</span>}
                      </div>
                    </div>
                    {t.description && <p className="text-xs text-[#5C5955] font-['Work_Sans'] leading-relaxed mb-2">{t.description}</p>}
                    {t.body_response && (
                      <div className="text-[11px] text-[#8A8681] font-['Work_Sans'] space-y-1 mt-2 p-3 rounded-lg bg-[#F8F6F3]">
                        {t.body_response && <p><span className="text-[#8C6B55]">Body:</span> {t.body_response}</p>}
                        {t.thought_pattern && <p><span className="text-[#5C6B8A]">Thought:</span> {t.thought_pattern}</p>}
                        {t.emotional_response && <p><span className="text-[#9B7BC8]">Feeling:</span> {t.emotional_response}</p>}
                      </div>
                    )}
                    {t.desired_corrected_response && (
                      <div className="mt-2 p-3 rounded-lg bg-[#4E7A6B]/[0.04] border-l-2 border-[#4E7A6B]/20">
                        <p className="text-[10px] text-[#4E7A6B]/60 font-['Work_Sans'] uppercase tracking-wider mb-0.5">Corrected Response</p>
                        <p className="text-xs text-[#4E7A6B] font-['Work_Sans']">{t.desired_corrected_response}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* ════ WEEKLY DIGEST TAB ════ */}
            {tab === 'digest' && digest && (
              <div className="space-y-5" data-testid="digest-tab">
                <div className="text-center mb-2">
                  <p className="text-[10px] text-[#8A8681] font-['Work_Sans']">{digest.period}</p>
                </div>

                {/* Key metrics */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-xl border border-[#EBE8E3] p-4 text-center">
                    <p className="text-2xl font-['Outfit'] font-light text-[#4E7A6B]">{digest.stability_pct}%</p>
                    <p className="text-[9px] uppercase tracking-wider text-[#8A8681]">Stability</p>
                    {digest.stability_trend !== 'steady' && (
                      <p className={`text-[9px] mt-0.5 ${digest.stability_trend === 'improving' ? 'text-[#4E7A6B]' : 'text-[#C85C5C]'}`}>
                        {digest.stability_trend === 'improving' ? '↑' : '↓'} vs last week
                      </p>
                    )}
                  </div>
                  <div className="rounded-xl border border-[#EBE8E3] p-4 text-center">
                    <p className="text-2xl font-['Outfit'] font-light text-[#8C6B55]">{digest.total_sessions}</p>
                    <p className="text-[9px] uppercase tracking-wider text-[#8A8681]">Sessions</p>
                  </div>
                  <div className="rounded-xl border border-[#EBE8E3] p-4 text-center">
                    <p className="text-2xl font-['Outfit'] font-light text-[#5C6B8A]">{digest.session_adherence}%</p>
                    <p className="text-[9px] uppercase tracking-wider text-[#8A8681]">Adherence</p>
                  </div>
                </div>

                {/* Dominant pattern */}
                {digest.dominant_pattern_info && (
                  <div className="rounded-xl border border-[#EBE8E3] p-4">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-[#8A8681] font-['Work_Sans'] mb-2">Dominant Pattern This Week</p>
                    <p className="text-sm font-['Work_Sans'] font-medium text-[#2C2A29] mb-1">{digest.dominant_pattern_info.label}</p>
                    <p className="text-xs text-[#5C5955] font-['Work_Sans'] leading-relaxed">{digest.dominant_pattern_info.explanation}</p>
                  </div>
                )}

                {/* Top triggers */}
                {digest.dominant_triggers?.length > 0 && (
                  <div className="rounded-xl border border-[#EBE8E3] p-4">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-[#8A8681] font-['Work_Sans'] mb-3">Dominant Triggers</p>
                    {digest.dominant_triggers.map((t, i) => (
                      <div key={i} className="flex items-center justify-between py-1.5">
                        <span className="text-xs text-[#5C5955] font-['Work_Sans'] capitalize">{t.trigger.replace('_', ' ')}</span>
                        <span className="text-[10px] text-[#8C6B55] font-mono">{t.count}x</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Improvements */}
                {digest.improvements?.length > 0 && (
                  <div className="rounded-xl bg-[#4E7A6B]/[0.04] border border-[#4E7A6B]/10 p-4">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-[#4E7A6B]/60 font-['Work_Sans'] mb-2">Improvements</p>
                    {digest.improvements.map((imp, i) => (
                      <div key={i} className="flex items-start gap-2 mb-1.5">
                        <Check className="w-3 h-3 text-[#4E7A6B] mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-[#4E7A6B] font-['Work_Sans']">{imp}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Concerns */}
                {digest.concerns?.length > 0 && (
                  <div className="rounded-xl bg-[#C85C5C]/[0.04] border border-[#C85C5C]/10 p-4">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-[#C85C5C]/60 font-['Work_Sans'] mb-2">Needs Attention</p>
                    {digest.concerns.map((c, i) => (
                      <div key={i} className="flex items-start gap-2 mb-1.5">
                        <AlertCircle className="w-3 h-3 text-[#C85C5C] mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-[#C85C5C] font-['Work_Sans']">{c}</p>
                      </div>
                    ))}
                  </div>
                )}

                {digest.total_entries === 0 && (
                  <div className="text-center py-12 rounded-xl bg-[#F8F6F3] border border-[#EBE8E3]">
                    <p className="text-sm text-[#8A8681] font-['Work_Sans']">Complete check-ins this week to generate your weekly intelligence</p>
                  </div>
                )}
              </div>
            )}
            {tab === 'digest' && !digest && (
              <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-[#C89B7B] animate-spin" /></div>
            )}
          </>
        )}
      </div>
    </BiologicalLayout>
  );
}
